<template>
    <div>
        <h2
            v-t="'Teamzugehörigkeiten'"
            class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
        ></h2>
        <table class="kgr-table kgr-table--striped table-groups kgr-row kgr-table--block teams">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('team') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Start') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Ende') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="teamRelation in teamMemberRelations"
                    :key="teamRelation.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ teamRelation.team.name }}
                        <div class="department-name">
                            ({{ teamRelation.team.departmentName }})
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(teamRelation)">
                            <div
                                class="form-widget without-label date-picker"
                                :class="{ invalid: !isValidDateRange(editRelations[getEditIndex(teamRelation)]) }"
                            >
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(teamRelation)].start"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ moment(teamRelation.start).format('DD.MM.YYYY') }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(teamRelation)">
                            <div
                                class="form-widget without-label date-picker"
                                :class="{ invalid: !isValidDateRange(editRelations[getEditIndex(teamRelation)]) }"
                            >
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(teamRelation)].end"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ teamRelation.formattedEnd() }}
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <button
                            v-if="isRelationEditable(teamRelation)"
                            type="button"
                            class="kgr-table__action"
                            :disabled="isBeingEdited(teamRelation)"
                            @click="edit(teamRelation)"
                        >
                            <kgr-icon-edit
                                :size="15"
                                :disabled="isBeingEdited(teamRelation)"
                            />
                        </button>
                        <button
                            v-if="isRelationDeletable(teamRelation)"
                            type="button"
                            class="kgr-table__action"
                            @click="remove(teamRelation)"
                        >
                            <kgr-icon-delete />
                        </button>
                    </td>
                </tr>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-team-select
                            v-model="newRelation.team"
                            :club-id="clubId"
                            hide-label
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newRelation.isEmpty() && (!newRelation.start || !isValidDateRange(newRelation)) }"
                        >
                            <kgr-date-picker v-model="newRelation.start" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newRelation.isEmpty() && !isValidDateRange(newRelation) }"
                        >
                            <kgr-date-picker v-model="newRelation.end" />
                        </div>
                    </td><td class="kgr-table__cell"></td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="isCreatable || isEditable"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import DatePicker from '../common/form/picker/DatePicker';
import Error from '../../js/model/Error';
import IconDelete from '../common/icon/IconDelete';
import IconEdit from '../common/icon/IconEdit';
import KgrButton from '../common/form/button/KgrButton';
import MemberRepository from '../../js/repository/MemberRepository';
import TeamMemberRelation from '../../js/dto/TeamMemberRelation';
import TeamSelect from '../common/form/select/TeamSelect';

export default {
    components: {
        'kgr-team-select': TeamSelect,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-date-picker': DatePicker,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        teamMemberRelations: {
            type: Array,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            newRelation: new TeamMemberRelation(),
            editRelations: [],
            pendingCreate: false,
            pendingUpdate: false,
        };
    },
    computed: {
        isSavable() {
            if (this.editRelations.length === 0 && this.newRelation.isEmpty()) {
                return false;
            }

            const hasInvalidEdits = this.editRelations.filter((editRelation) => !this.isValidDateRange(editRelation)).length > 0;
            if (hasInvalidEdits) {
                return false;
            }

            if (!this.newRelation.isEmpty() && !this.newRelation.isValid()) {
                return false;
            }

            return true;
        },
    },
    methods: {
        isBeingEdited(teamRelation) {
            return this.getEditIndex(teamRelation) !== -1;
        },
        getEditIndex(teamRelation) {
            return this.editRelations.map((editRelation) => editRelation.id).indexOf(teamRelation.id);
        },
        isValidDateRange(teamRelation) {
            if (!teamRelation.end) {
                return true;
            }

            return this.moment(teamRelation.end, 'DD.MM.YYYY') >= this.moment(teamRelation.start, 'DD.MM.YYYY');
        },
        remove(teamRelation) {
            showConfirm(this.$t('confirm'), this.$t('team.relation.confirm.delete', { name: teamRelation.team.name }), () => {
                this.editRelations = this.editRelations.filter((r) => r.id !== teamRelation.id);
                MemberRepository
                    .removeTeam(this.memberId, teamRelation.id)
                    .then(() => {
                        this.$notify.success('team.relation.delete.success', { name: teamRelation.team.name });
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(teamRelation) {
            if (this.isBeingEdited(teamRelation)) {
                return;
            }

            this.editRelations.push(teamRelation.toData());
        },
        save() {
            if (!this.newRelation.isEmpty()) {
                this.create();
            }

            if (this.editRelations.length > 0) {
                this.update();
            }
        },
        create() {
            this.pendingCreate = true;
            MemberRepository
                .createTeamRelation(this.memberId, this.newRelation)
                .then(() => {
                    this.newRelation = new TeamMemberRelation();
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error('team.relation.saveChanges.conflict', { name: this.newRelation.team.name });
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
                .finally(() => this.pendingCreate = false);
        },
        update() {
            this.pendingUpdate = true;
            MemberRepository
                .updateTeamRelations(this.memberId, this.editRelations)
                .then(() => {
                    this.editRelations = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingUpdate = false);
        },
        emitUpdated() {
            this.$emit('updated');
        },
        isRelationEditable(teamMemberRelation) {
            return this.isEditable && teamMemberRelation.isTeamViewable;
        },
        isRelationDeletable(teamMemberRelation) {
            return this.isDeletable && teamMemberRelation.isTeamViewable;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_save {
    display: block;
    margin-left: auto;
}

div .date-picker {
    border: 1px solid transparent;

    &.invalid {
        border: 1px solid #f44336;
    }
}

.department-name {
    font-size: 80%;
}
</style>
